import React from 'react';
import { Box, Flex, Icon, Text } from '@cad-arc';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    UniqueIdentifier,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const HANDLE_WIDTH = 'var(--arc-space-6)';
const ROW_GAP = 'var(--arc-space-2)';
const ROW_GAP_MD = 'var(--arc-space-3)';

export function getReorderedItems<T>(
    items: T[],
    getId: (item: T) => string,
    activeId: UniqueIdentifier,
    overId: UniqueIdentifier | undefined
): T[] | null {
    if (!overId || activeId === overId) {
        return null;
    }
    const oldIndex = items.findIndex((item) => getId(item) === activeId);
    const newIndex = items.findIndex((item) => getId(item) === overId);
    if (oldIndex === -1 || newIndex === -1) {
        return null;
    }
    return arrayMove(items, oldIndex, newIndex);
}

export function SortableList<T>({
    items,
    getId,
    renderItem,
    onReorder,
    handlePosition = 'left',
    firstItemLabel,
    dragHandleSize = 'small',
}: {
    items: T[];
    getId: (item: T) => string;
    renderItem: (item: T) => React.ReactNode;
    onReorder: (newOrder: T[]) => void;
    handlePosition?: 'left' | 'right';
    firstItemLabel?: string;
    dragHandleSize?: 'small' | 'medium';
}): React.ReactElement {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        const reordered = getReorderedItems(items, getId, active.id, over?.id);
        if (reordered) {
            onReorder(reordered);
        }
    }

    const showHandle = items.length > 1;

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(getId)} strategy={verticalListSortingStrategy}>
                {items.map((item, index) => {
                    const id = getId(item);
                    return (
                        <SortableListRow
                            key={id}
                            id={id}
                            handlePosition={handlePosition}
                            showHandle={showHandle}
                            label={index === 0 ? firstItemLabel : undefined}
                            dragHandleSize={dragHandleSize}
                        >
                            {renderItem(item)}
                        </SortableListRow>
                    );
                })}
            </SortableContext>
        </DndContext>
    );
}

function SortableListRow({
    id,
    handlePosition,
    showHandle,
    label,
    children,
    dragHandleSize,
}: {
    id: string;
    handlePosition: 'left' | 'right';
    showHandle: boolean;
    label?: string;
    children: React.ReactNode;
    dragHandleSize: 'small' | 'medium';
}): React.ReactElement {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const dragHandle = (
        <Box position="relative" width={HANDLE_WIDTH} alignSelf="stretch" flexShrink={0}>
            <Box
                as="span"
                data-testid={`sortable-list-handle-${id}`}
                cursor="grab"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding={dragHandleSize === 'medium' ? ROW_GAP_MD : ROW_GAP}
                {...attributes}
                {...listeners}
            >
                <Icon
                    icon="DragHandle"
                    size={dragHandleSize === 'medium' ? 'md' : 'sm'}
                    color="brand.default"
                />
            </Box>
        </Box>
    );

    return (
        <Box
            ref={setNodeRef}
            data-testid={`sortable-list-row-${id}`}
            width="100%"
            style={{ transform: CSS.Translate.toString(transform), transition }}
        >
            {label ? (
                <Box
                    paddingLeft={
                        showHandle && handlePosition === 'left'
                            ? `calc(${HANDLE_WIDTH} + ${ROW_GAP})`
                            : undefined
                    }
                >
                    <Text variant="caption" fontWeight="bold" color="text.primary">
                        {label}
                    </Text>
                </Box>
            ) : undefined}
            <Flex
                width="100%"
                alignItems="stretch"
                gap={ROW_GAP}
                paddingTop={ROW_GAP}
                borderRadius="md"
                bg={isDragging ? 'interactive.hover' : undefined}
            >
                {showHandle && handlePosition === 'left' && dragHandle}
                <Box flex="1" minWidth="0">
                    {children}
                </Box>
                {showHandle && handlePosition === 'right' && dragHandle}
            </Flex>
        </Box>
    );
}
